import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '../stores/useAuthStore';
import { useCartStore } from '../stores/useCartStore';
import { Calendar, Clock, ArrowRight, Info, ChevronDown } from 'lucide-react';
import logo from "../media/cy-logo-negative.png";
import logotk from "../media/cy_take_away.png";

export function OrderTimingPage() {
    const { tableId } = useParams<{ tableId: string }>();
    const navigate = useNavigate();
    const { setOrderTiming, isTakeAway, isOpen, config, fetchConfig } = useAuthStore();
    const [isHoursVisible, setIsHoursVisible] = useState(false);

    useEffect(() => {
        // Limpiar carrito al entrar para cambiar el flujo
        useCartStore.getState().clearAll();

        if (isTakeAway === false) {
            navigate(`/dashboard/${tableId}`, { replace: true });
            return;
        }

        // Refresh config when entering this page to have latest schedule
        fetchConfig();
    }, [isTakeAway, navigate, tableId, fetchConfig]);

    const handleSelect = (timing: 'today' | 'week') => {
        if (timing === 'today' && !isOpen) return;
        if (timing === 'week' && config && !config.accept_scheduled_orders) return;

        setOrderTiming(timing);
        if (isTakeAway) {
            navigate(`/order-mode/${tableId}`);
        } else {
            navigate(`/dashboard/${tableId}`);
        }
    };

    const dayNames: Record<number, string> = {
        0: "Lunes",
        1: "Martes",
        2: "Miércoles",
        3: "Jueves",
        4: "Viernes",
        5: "Sábado",
        6: "Domingo"
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4 py-12">
            <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-xl max-w-md w-full border border-gray-100 dark:border-gray-700">
                <div className="text-center mb-10">
                    <div className="relative mx-auto mb-6 w-24 h-24 rounded-full bg-gradient-to-br from-sky-400 to-indigo-500 p-1">
                        <div className="w-full h-full rounded-full bg-white dark:bg-gray-800 flex items-center justify-center overflow-hidden">
                            <img src={isTakeAway ? logotk : logo} alt="Logo" className="w-16 h-16 object-contain" />
                        </div>
                    </div>
                    <h1 className="text-2xl font-black text-gray-900 dark:text-white mb-2">
                        ¿Para cuándo es tu pedido?
                    </h1>
                    <p className="text-gray-500 dark:text-gray-400 text-sm">
                        Elegí si querés comer ahora o planificar tu semana
                    </p>
                </div>

                {!isOpen && (
                    <div className="mb-8 p-6 bg-rose-50 dark:bg-rose-900/10 border border-rose-200 dark:border-rose-800 rounded-2xl animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-xl bg-rose-500 text-white flex items-center justify-center shrink-0">
                                <Clock className="w-6 h-6" />
                            </div>
                            <div>
                                <h4 className="font-bold text-rose-900 dark:text-rose-100 text-sm">Estamos fuera de horario</h4>
                                <p className="text-xs text-rose-700 dark:text-rose-300">Consultá nuestros horarios de atención abajo.</p>
                            </div>
                        </div>

                        <button
                            onClick={() => setIsHoursVisible(!isHoursVisible)}
                            className="w-full py-2.5 px-4 bg-rose-100/50 dark:bg-rose-900/20 rounded-xl text-xs font-bold text-rose-800 dark:text-rose-200 flex items-center justify-between transition-all"
                        >
                            Ver horarios de la semana
                            <ChevronDown className={`w-4 h-4 transition-transform ${isHoursVisible ? 'rotate-180' : ''}`} />
                        </button>

                        {isHoursVisible && (
                            <div className="mt-4 space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                                {[0, 1, 2, 3, 4, 5, 6].map((day) => {
                                    const ranges = config?.working_hours?.[day as keyof typeof config.working_hours] || [];
                                    return (
                                        <div key={day} className="flex justify-between items-center text-[10px] py-1.5 border-b border-rose-200/10 last:border-0">
                                            <span className="font-semibold text-rose-200/80 uppercase tracking-widest">{dayNames[day]}</span>
                                            <span className="font-mono font-bold text-rose-100">
                                                {ranges.length > 0
                                                    ? ranges.map((r: any) => `${r.start} - ${r.end}`).join(', ')
                                                    : <span className="text-rose-400 font-bold">Cerrado</span>
                                                }
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                <div className="grid gap-4">
                    <button
                        onClick={() => handleSelect('today')}
                        disabled={!isOpen}
                        className={`group relative border rounded-2xl p-6 flex items-center gap-4 transition-all duration-300 shadow-sm ${!isOpen
                            ? 'bg-gray-100 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 opacity-60 grayscale cursor-not-allowed'
                            : 'bg-white dark:bg-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 hover:scale-[1.02] hover:shadow-md'
                            }`}
                    >
                        <div className={`w-14 h-14 rounded-xl flex items-center justify-center transition-transform ${!isOpen ? 'bg-gray-400 text-white' : 'bg-sky-500 text-white group-hover:scale-110'}`}>
                            <Clock className="w-8 h-8" />
                        </div>
                        <div className="flex-1 text-left">
                            <h3 className={`font-bold text-lg ${!isOpen ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-white'}`}>
                                Ordenar para Hoy
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                {isOpen ? 'Pedidos inmediatos' : 'Fuera de horario'}
                            </p>
                        </div>
                        {isOpen && <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-sky-500 transition-colors" />}
                    </button>

                    <button
                        onClick={() => handleSelect('week')}
                        disabled={!!config && !config.accept_scheduled_orders}
                        className={`group relative border rounded-2xl p-6 flex items-center gap-4 transition-all duration-300 shadow-sm ${config && !config.accept_scheduled_orders
                            ? 'bg-gray-100 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 opacity-60 grayscale cursor-not-allowed'
                            : 'bg-white dark:bg-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 hover:scale-[1.02] hover:shadow-md'
                            }`}
                    >
                        <div className={`w-14 h-14 rounded-xl flex items-center justify-center transition-transform ${config && !config.accept_scheduled_orders ? 'bg-gray-400 text-white' : 'bg-indigo-500 text-white group-hover:scale-110'}`}>
                            <Calendar className="w-8 h-8" />
                        </div>
                        <div className="flex-1 text-left">
                            <h3 className={`font-bold text-lg ${config && !config.accept_scheduled_orders ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-white'}`}>
                                Para la semana
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                {config && !config.accept_scheduled_orders ? 'No disponible' : 'Programá tus comidas'}
                            </p>
                        </div>
                        {(!config || config.accept_scheduled_orders) && <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-indigo-500 transition-colors" />}
                    </button>
                </div>

                <div className="mt-8 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800 rounded-2xl p-4 flex gap-3">
                    <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-800 dark:text-amber-200 leading-relaxed font-medium">
                        <strong>Atención:</strong> Los pedidos programados se abonan por adelantado únicamente con <strong>Billeteras Virtuales</strong>.
                    </p>
                </div>
            </div>
        </div>
    );
}
